import type { IrisChip } from "./iris-watch";

/**
 * Iris checks your film: everything that stands between this production and its next step,
 * read for free from the production's own status. Each finding comes with the chips that solve
 * it through the studio's own actions — a paid step still shows its price before anything runs.
 */
export type FindingKind = "cast" | "place" | "plan" | "story" | "decision" | "review" | "picture" | "job" | "repaired";

export interface IrisFinding {
  /** Stable while the problem lasts, so the walk-through keeps its place. */
  id: string;
  /** "ok" is the mechanic's green report of what it already repaired. */
  tone: "attention" | "verify" | "ok";
  kind: FindingKind;
  title: string;
  /** What Iris asks the maker, when the finding is a choice. */
  question?: string;
  chips: IrisChip[];
}

/** The storyboard review's own verdict per shot (0-based positions), computed by the studio. */
export interface ReviewSummary {
  attention: number[];
  verify: number[];
}

type Loose = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

const list = (value: unknown): Loose[] => (Array.isArray(value) ? (value as Loose[]) : []);
const count = (value: unknown): number =>
  Array.isArray(value) ? value.length : Number.isFinite(Number(value)) ? Number(value) : 0;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const shot = (position: number): IrisChip => ({ label: `Open shot ${position + 1}`, action: { scene: position } });
const REBUILD: IrisChip = { label: "Make an updated shot plan", action: { run: "rebuild-storyboard" }, primary: true };
const JOB_LABELS: Record<string, string> = {
  generate_avatar_batch: "singing shots",
  generate_avatar: "singing shot",
  prepare_singer_photo: "bokeh photo",
  analyze_audio: "song analysis",
  prepare_storyboard: "shot plan",
  generate_storyboard: "shot plan",
  generate_images: "picture",
  generate_background: "background",
  generate_motion: "animation",
  generate_motion_batch: "animation",
  generate_lipsync: "lip-sync",
  generate_lipsync_batch: "lip-sync",
  render_preview: "preview render",
  render_final: "final render",
};
const FREE_RETRY = new Set(["analyze_audio", "prepare_storyboard", "generate_storyboard"]);
/** Paid steps the studio can start again itself, with its price confirmation (job kind → studio route). */
const RETRY_ROUTES: Record<string, string> = {
  generate_images: "images",
  generate_motion_batch: "motion_batch",
  generate_thumbnail: "thumbnail",
  render_preview: "preview",
  render_final: "render",
};

/** What the mechanic did or saw when the production was opened (free, server-side). */
function mechanicFindings(p: Loose): IrisFinding[] {
  const report: Loose = p.mechanic || {};
  const repairs = list(report.repairs);
  // A ghost that was cleared and then restarted is one repair to the maker: the restart.
  const restarted = new Map(repairs.filter((repair) => repair.outcome === "resumed").map((repair) => [String(repair.job_kind), repair]));
  const merged = new Map<string, string[]>();
  for (const repair of repairs)
    if (repair.outcome === "finalized" && restarted.has(String(repair.job_kind))) {
      const key = String(restarted.get(String(repair.job_kind))!.id);
      merged.set(key, [...(merged.get(key) || []), String(repair.id)]);
    }
  const folded = new Set([...merged.values()].flat());
  const repaired = repairs.filter((repair) => !folded.has(String(repair.id))).map((repair) => {
    const label = JOB_LABELS[String(repair.job_kind)] || "production";
    return {
      id: `repair:${[String(repair.id), ...(merged.get(String(repair.id)) || [])].join("+")}`,
      tone: "ok" as const,
      kind: "repaired" as const,
      title: repair.outcome === "finalized" ? `I cleared a stopped ${label} step so the film can go on` : `I restarted the ${label} step; it had stopped`,
      chips: [],
    };
  });
  const hanging = list(report.hanging).map((job) => {
    const label = JOB_LABELS[String(job.kind)] || "production";
    const minutes = Number(job.minutes) || 0;
    return {
      id: `hanging:${job.job_id}`,
      tone: "verify" as const,
      kind: "job" as const,
      title: `The ${label} step has not responded for ${minutes} min`,
      chips: [{ label: "Ask Iris", action: { ask: `The ${label} step has not responded for ${minutes} minutes. What is going on, and should I wait?` }, primary: true }],
    };
  });
  return [...repaired, ...hanging];
}

function castFindings(p: Loose): IrisFinding[] {
  const go: IrisChip = { label: "Go to Cast & looks", action: { step: "visual" }, primary: true };
  const found: IrisFinding[] = [];
  const gaps = list(p.cast_gaps_unanswered);
  if (gaps.length)
    found.push({ id: "cast:gaps", tone: "attention", kind: "cast", title: `The story mentions ${plural(gaps.length, "character")} not in the cast yet`, chips: [go] });
  const waiting = list(p.production_assets).filter(
    (asset) => asset.enabled !== false && asset.selection_enabled !== false && asset.approval_status && asset.approval_status !== "approved",
  );
  for (const asset of waiting)
    found.push({ id: `cast:${asset.id || asset.name}`, tone: "attention", kind: "cast", title: `${asset.name || "A cast member"} waits for your approval`, chips: [go] });
  return found;
}

function placeFindings(p: Loose): IrisFinding[] {
  const plan: Loose = p.background_plan || {};
  const go: IrisChip = { label: "Go to Background", action: { step: "background" }, primary: true };
  return list(plan.locations)
    .filter((location) => list(location.scene_ids).length && !location.approved)
    .map((location) => ({
      id: `place:${location.id || location.name}`,
      tone: "attention" as const,
      kind: "place" as const,
      title: `${location.name || "A place"} still needs an approved background`,
      chips: [go],
    }));
}

function questionFindings(p: Loose): { found: IrisFinding[]; covered: number[] } {
  const intelligence: Loose = p.narrative_intelligence || {};
  const resolutionId = String(intelligence.context_resolution_id || "");
  const covered: number[] = [];
  const found = list(intelligence.context_resolution?.questions)
    .filter((question) => !question.answer && !question.answered_option_id)
    .map((question) => {
      const positions = list(question.scene_positions).map(Number).filter(Number.isInteger) as unknown as number[];
      covered.push(...positions);
      const chips: IrisChip[] = list(question.options).map((option) => ({
        label: String(option.label || option.id),
        primary: String(option.id) === String(question.recommended_option_id || ""),
        action: { answer: { resolutionId, questionId: String(question.id), optionId: String(option.id) } },
      }));
      if (positions.length) chips.push(shot(positions[0]!));
      return {
        id: `question:${question.id}`,
        tone: "attention" as const,
        kind: "story" as const,
        title: "A story question waits for you",
        question: String(question.prompt || "Choose the intended story context."),
        chips,
      };
    });
  return { found, covered };
}

/** Conflicts on shots that already carry a story question are solved by answering it. */
function conflictFindings(p: Loose, asked: number[]): { found: IrisFinding[]; covered: number[] } {
  const covered: number[] = [];
  const found = list(p.storyboard_validation?.conflicts).flatMap((conflict, index) => {
    const message = String(typeof conflict === "string" ? conflict : conflict.message || "This shot needs a decision.");
    const position = typeof conflict === "string" ? NaN : Number(conflict.scene_position);
    const known = Number.isInteger(position);
    if (known && asked.includes(position)) return [];
    if (known) covered.push(position);
    const ask: IrisChip = { label: "Ask Iris", action: { ask: `Help me decide: ${known ? `shot ${position + 1}: ` : ""}${message}` } };
    return {
      id: `conflict:${known ? position : "film"}:${index}`,
      tone: "attention" as const,
      kind: "decision" as const,
      title: known ? `Shot ${position + 1}: ${message}` : message,
      chips: known ? [{ ...shot(position), primary: true }, ask] : [{ ...ask, primary: true }],
    };
  });
  return { found, covered };
}

function reviewFindings(review: ReviewSummary, covered: number[]): IrisFinding[] {
  const found: IrisFinding[] = [];
  const attention = review.attention.filter((position) => !covered.includes(position));
  // One finding for Review with Iris: red and yellow together, red sets the tone.
  const all = [...attention, ...review.verify].sort((a, b) => a - b);
  if (all.length)
    found.push({
      id: "review:checks",
      tone: attention.length ? "attention" : "verify",
      kind: "review",
      title: `${plural(all.length, "shot")} to review`,
      chips: [{ label: "Review", action: { review: true }, primary: true }, shot(all[0]!)],
    });
  return found;
}

function jobFindings(p: Loose): IrisFinding[] {
  const retryable = new Set(list(p.mechanic?.retry).map((item) => String(item.kind)));
  const latest = new Map<string, Loose>();
  const ordered = list(p.jobs)
    .slice()
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  for (const job of ordered) latest.set(String(job.kind), job);
  return [...latest.values()]
    .filter((job) => job.status === "failed")
    .map((job) => {
      const kind = String(job.kind);
      const label = JOB_LABELS[kind] || "production";
      const ask: IrisChip = { label: "Ask Iris what happened", action: { ask: `The ${label} step stopped: ${String(job.error || "no reason given")}. What happened, and what should I do?` } };
      return {
        id: `job:${job.id || kind}`,
        tone: "attention" as const,
        kind: "job" as const,
        title: `The ${label} step stopped`,
        question: job.error ? String(job.error).slice(0, 200) : undefined,
        chips: FREE_RETRY.has(kind)
          ? [{ ...REBUILD, label: "Try again" }, ask]
          : retryable.has(kind) && RETRY_ROUTES[kind]
            ? [{ label: "Try again", action: { run: "retry" as const, kind: RETRY_ROUTES[kind]! }, primary: true }, ask]
            : [{ ...ask, primary: true }],
      };
    });
}

/** Everything Iris found in this production, in the order the film needs it solved. */
export function irisScan(project: unknown, review: ReviewSummary = { attention: [], verify: [] }): IrisFinding[] {
  if (!project || typeof project !== "object") return [];
  const p = project as Loose;
  const jobs = [...mechanicFindings(p), ...jobFindings(p)];
  // Before the shot plan exists, open cast and places are the current work, not something to fix.
  if (!list(p.scenes).length) return jobs;
  // Sing with your photo has no cast, places or story review: only the photo route's own steps,
  // which Iris's note leads, and what the mechanic reports.
  if ((p.creative_brief as Loose | undefined)?.selected_production_option === "photo_session") return jobs;
  const found: IrisFinding[] = [...castFindings(p), ...placeFindings(p)];
  if (list(p.storyboard_content_warnings).length)
    found.push({ id: "plan:content", tone: "attention", kind: "plan", title: "Some headings were saved as shots", chips: [REBUILD] });
  else if (String(p.storyboard_status) === "stale")
    found.push({ id: "plan:stale", tone: "attention", kind: "plan", title: "The story, cast or timing changed since this shot plan", chips: [REBUILD] });
  const questions = questionFindings(p), conflicts = conflictFindings(p, questions.covered);
  found.push(...questions.found, ...conflicts.found, ...reviewFindings(review, [...questions.covered, ...conflicts.covered]));
  const outdated = count(p.outdated_images);
  if (outdated)
    found.push({
      id: "picture:outdated",
      tone: "attention",
      kind: "picture",
      title: `${plural(outdated, "picture")} ${outdated === 1 ? "is" : "are"} out of date`,
      question: "A cast member, look or place changed after these were made. Remaking shows the price first.",
      chips: [{ label: outdated === 1 ? "Remake it" : "Remake them", action: { run: "images" } }],
    });
  // The mechanic's report comes first: what Iris already repaired, then what still hangs.
  const mechanicFirst = jobs.filter((finding) => finding.kind === "repaired" || finding.id.startsWith("hanging:"));
  return [...mechanicFirst, ...found, ...jobs.filter((finding) => !mechanicFirst.includes(finding))];
}
